import {
  CdkDrag,
  CdkDropList,
  moveItemInArray,
  type CdkDragDrop,
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';

import { type IMultiSelectItem } from '../../questions/question-multi-base';

export const MULTI_SELECT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectComponent),
  multi: true,
};

@Component({
  selector: 'myre-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MULTI_SELECT_VALUE_ACCESSOR],
  imports: [CdkDropList, CdkDrag, MatCheckbox],
})
export class MultiSelectComponent<T> implements ControlValueAccessor {
  private _changeDetectionRef = inject(ChangeDetectorRef);

  public get items(): IMultiSelectItem<T>[] {
    return (this._items || []).filter(i => !i.hidden);
  }

  @Input()
  public set items(items: IMultiSelectItem<T>[]) {
    this._items = (items || []).sort((a, b) => a.position - b.position);
  }

  @Output()
  public dragged = new EventEmitter<IMultiSelectItem<T>[]>();

  @Input()
  public readonly = false;

  @Input()
  public draggable = false;

  @Input()
  public color = 'primary';

  public disabled = false;

  private _value: T[] = [];
  private _items: IMultiSelectItem[];

  public registerOnChange(fn: () => any): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public writeValue(items: T[]): void {
    this._value = items ? [...items] : items;
    this._changeDetectionRef.detectChanges();
  }

  public isChecked(item: IMultiSelectItem<T>): boolean {
    return this._value?.includes(item.value);
  }

  public isDraggable(item: IMultiSelectItem<T>): boolean {
    return this.draggable && !item.disabled && !this.readonly;
  }

  public onChange(checked: boolean, value: T, index: number): void {
    if (this.items[index].disabled) {
      return;
    }

    this._value = this.items
      .filter(item => {
        if (item.value === value) {
          return checked;
        }

        return this._value?.includes(item.value);
      })
      .map(item => item.value);

    this._onModelChange(this._value);
  }

  public onBlur(): void {
    this._onTouched();
  }

  public moveItem(evt: CdkDragDrop<IMultiSelectItem<T>[]>): void {
    const newItems = [...this._items];

    // since DnD container do not have hidden elements, index do not match
    // previous index is the index in full array with ref. comparison (===)
    // next index is the index in DnD container + number of hidden elements before the item in full array
    const previousIndex = newItems.findIndex(
      i => i.value === evt.item.data.value,
    );
    let offset = 0;

    for (const item of this._items) {
      if (item.value === evt.item.data.value) {
        break;
      }

      if (item.hidden) {
        offset++;
      }
    }

    moveItemInArray(newItems, previousIndex, evt.currentIndex + offset);

    this.dragged.emit(newItems);

    const newValues = newItems
      .filter(item => this._value?.includes(item.value))
      .map(item => item.value);

    this._onModelChange(newValues);
  }

  private _onModelChange: (value: T[]) => any = () => {};

  private _onTouched: () => any = () => {};
}
